import { createSlice } from "@reduxjs/toolkit";
import { LAYOUT_TOTALS } from "layout/usageStats/constants";

import { fetchAllWorkspaces } from "./actions";

export const initialState = {
  data: null,
  dataDetail: LAYOUT_TOTALS,
  isLoading: false,
  error: null,
  filters: {
    accountType: "docs_subscription",
    source: "all",
    range: "all",
    from: null,
    to: null,
    q: "",
  },
  exportIsLoading: false,
  exportError: null,
  selectedWorkspaceId: null,
};

const DEFAULT_ERROR = "Failed to load the cloud usage workspace list";
const DEFAULT_EXPORT_ERROR = "Failed to export the cloud usage workspace list";

const cloudUsageSlice = createSlice({
  name: "cloudUsage",
  initialState,
  reducers: {
    setAccountTypeFilter: (state, action) => {
      state.filters.accountType = action.payload;
    },
    setSourceFilter: (state, action) => {
      state.filters.source = action.payload;
    },
    setRangeFilter: (state, action) => {
      const { range, from, to } = action.payload;
      state.filters.range = range;
      state.filters.from = from ?? null;
      state.filters.to = to ?? null;
    },
    setSearchQuery: (state, action) => {
      state.filters.q = action.payload;
    },
    resetCloudUsageFilters: (state) => {
      state.filters = initialState.filters;
    },
    exportWorkspacesStarted: (state) => {
      state.exportIsLoading = true;
      state.exportError = null;
    },
    exportWorkspacesFinished: (state) => {
      state.exportIsLoading = false;
    },
    exportWorkspacesFailed: (state, action) => {
      state.exportIsLoading = false;
      state.exportError = action.payload ?? DEFAULT_EXPORT_ERROR;
    },
    setSelectedWorkspaceId: (state, action) => {
      state.selectedWorkspaceId = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllWorkspaces.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // The requested detail level is stored alongside the payload it produced. `sections` is keyed
      // by area alone in totals mode and by "AREA:SUB_AREA" in detailed mode, so a table rendered
      // against the wrong level misreads every activity column. Consumers pick the table from this,
      // not from the toggle, which flips a render before the matching refetch is even dispatched.
      .addCase(fetchAllWorkspaces.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
        state.dataDetail = action.meta.arg?.detail ?? LAYOUT_TOTALS;
      })
      .addCase(fetchAllWorkspaces.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message ?? DEFAULT_ERROR;
      });
  },
});

export const {
  setAccountTypeFilter,
  setSourceFilter,
  setRangeFilter,
  setSearchQuery,
  resetCloudUsageFilters,
  exportWorkspacesStarted,
  exportWorkspacesFinished,
  exportWorkspacesFailed,
  setSelectedWorkspaceId,
} = cloudUsageSlice.actions;

export default cloudUsageSlice.reducer;
